import { Box, Button, CheckboxInput, Modal, TextareaInput } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useState } from 'react';

import { VendorEquipmentTypes } from 'types';

interface GrantEquipmentExceptionModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSubmit: (data: { equipmentTypes: VendorEquipmentTypes[]; note?: string }) => Promise<void>;
  onRemove?: () => Promise<void>;
  existingTypes?: VendorEquipmentTypes[];
  existingNote?: string;
}

interface EquipmentTypeOption {
  label: string;
  value: VendorEquipmentTypes;
}

const equipmentTypeOptions: EquipmentTypeOption[] = [
  { label: 'Modules', value: VendorEquipmentTypes.panel },
  { label: 'Inverters', value: VendorEquipmentTypes.inverter },
  { label: 'Storage', value: VendorEquipmentTypes.storage },
  { label: 'Mounting', value: VendorEquipmentTypes.mounting },
];

export const GrantEquipmentExceptionModal = ({
  isOpen,
  onDismiss,
  onSubmit,
  onRemove,
  existingTypes = [],
  existingNote = '',
}: GrantEquipmentExceptionModalProps) => {
  const [selectedTypes, setSelectedTypes] = useState<VendorEquipmentTypes[]>(existingTypes);
  const [note, setNote] = useState(existingNote);
  const [isLoading, setIsLoading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const hasExistingException = existingTypes.length > 0;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTypes(existingTypes);
      setNote(existingNote);
      setIsLoading(false);
      setIsRemoving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleCheckboxChange = useCallback((type: VendorEquipmentTypes, isChecked: boolean) => {
    setSelectedTypes((prev) => {
      if (isChecked) {
        return [...prev, type];
      }
      return prev.filter((t) => t !== type);
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (selectedTypes.length === 0) return;

    setIsLoading(true);
    try {
      await onSubmit({ equipmentTypes: selectedTypes, note: note.trim() || undefined });
    } finally {
      setIsLoading(false);
    }
    onDismiss();
  }, [selectedTypes, note, onSubmit, onDismiss]);

  const handleRemove = useCallback(async () => {
    if (!onRemove) return;

    setIsRemoving(true);
    try {
      await onRemove();
    } finally {
      setIsRemoving(false);
    }
    onDismiss();
  }, [onRemove, onDismiss]);

  const handleCancel = useCallback(() => {
    setSelectedTypes(existingTypes);
    setNote(existingNote);
    onDismiss();
  }, [existingTypes, existingNote, onDismiss]);

  const isAnyActionInProgress = isLoading || isRemoving;

  return (
    <Modal isOpen={isOpen} onDismiss={handleCancel} maxWidth="4xl" ariaLabelledBy="grantEquipmentException">
      <Modal.Header
        id="grantEquipmentException"
        title={hasExistingException ? 'Modify Equipment Exception' : 'Grant Equipment Exception'}
        onDismiss={handleCancel}
      />
      <Modal.Body background="secondary" childGap="lg">
        <Box fontSize="sm" color="body-primary" data-testid="grant-equipment-exception-modal-body">
          Select the equipment types that should be granted an exception to use non-AVL equipment:
        </Box>
        <Box gap="md" data-testid="equipment-type-checkboxes">
          {equipmentTypeOptions.map((option) => (
            <CheckboxInput
              key={option.value}
              id={`equipment-type-${option.value}`}
              label={option.label}
              name={option.value}
              isChecked={selectedTypes.includes(option.value)}
              isDisabled={isAnyActionInProgress}
              onChange={(event) => handleCheckboxChange(option.value, event.target.checked)}
              data-testid={`checkbox-${option.value}`}
            />
          ))}
        </Box>
        <Box>
          <TextareaInput
            id="equipment-exception-note"
            name="note"
            label="Note (optional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            isDisabled={isAnyActionInProgress}
            placeholder="Add any additional context or reason for granting this exception..."
            rows={3}
            data-testid="equipment-exception-note"
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Box direction="row" gap="sm" justifyContent="space-between" width="100">
          <Box>
            {hasExistingException && onRemove && (
              <Button
                variant="secondary"
                tone="danger"
                onClick={handleRemove}
                isDisabled={isAnyActionInProgress}
                isLoading={isRemoving}
                data-testid="grant-equipment-exception-modal-remove-cta"
              >
                Remove Exception
              </Button>
            )}
          </Box>
          <Box direction="row" gap="sm">
            <Button
              variant="secondary"
              tone="neutral"
              onClick={handleCancel}
              isDisabled={isAnyActionInProgress}
              data-testid="grant-equipment-exception-modal-cancel-cta"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              tone="primary"
              onClick={handleSubmit}
              isDisabled={selectedTypes.length === 0 || isAnyActionInProgress}
              isLoading={isLoading}
              data-testid="grant-equipment-exception-modal-submit-cta"
            >
              {hasExistingException ? 'Update Exception' : 'Grant Exception'}
            </Button>
          </Box>
        </Box>
      </Modal.Footer>
    </Modal>
  );
};
